import { format as formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { parseISO } from 'date-fns'; // parseISO se usa explícitamente en algunos casos.
import { enUS } from 'date-fns/locale'; // Importar el locale

const TIME_ZONE = 'Europe/Madrid';

/**
 * Formatea una fecha (string ISO UTC o Date object) a un string legible en la zona horaria de Madrid.
 * Ejemplo: "26/10/2023 12:00"
 * @param {string | Date} dateInput - La fecha en formato string ISO UTC o un objeto Date (asumido UTC si es string).
 * @param {string} formatString - El formato deseado (ej: 'dd/MM/yyyy HH:mm').
 * @returns {string} - La fecha formateada o '-' si la entrada es inválida.
 */
export const formatDateToMadridReadable = (dateInput, formatString = 'dd/MM/yyyy HH:mm') => {
  if (!dateInput) return '-';
  try {
    console.log('[dateUtils] Input para toZonedTime (Readable):', dateInput);
    const zonedDate = toZonedTime(dateInput, TIME_ZONE);
    console.log('[dateUtils] zonedDate object (Readable):', zonedDate);

    if (!(zonedDate instanceof Date) || isNaN(zonedDate.getTime())) {
      console.error('[dateUtils] zonedDate (Readable) no es un objeto Date válido:', zonedDate);
      return '-';
    }
    
    // Para el formato 'dd/MM/yyyy HH:mm'
    const day = String(zonedDate.getDate()).padStart(2, '0');
    const month = String(zonedDate.getMonth() + 1).padStart(2, '0');
    const year = zonedDate.getFullYear();
    const hours = String(zonedDate.getHours()).padStart(2, '0');
    const minutes = String(zonedDate.getMinutes()).padStart(2, '0');

    if (formatString === 'dd/MM/yyyy HH:mm') {
      const result = `${day}/${month}/${year} ${hours}:${minutes}`;
      console.log('[dateUtils] Resultado manual (Readable):', result);
      return result;
    } else {
      console.warn('[dateUtils] formatDateToMadridReadable: formatString no soportado por la construcción manual, usando formatInTimeZone como fallback:', formatString);
      return formatInTimeZone(zonedDate, TIME_ZONE, formatString, { locale: enUS });
    }

  } catch (error) {
    console.error('[dateUtils] Error en formatDateToMadridReadable con input:', dateInput, error);
    return '-';
  }
};

/**
 * Convierte una fecha (string ISO UTC o Date object) a un string YYYY-MM-DDTHH:MM en la zona horaria de Madrid.
 * Ideal para rellenar inputs datetime-local.
 * @param {string | Date} dateInput - La fecha en formato string ISO UTC o un objeto Date.
 * @returns {string} - La fecha formateada para datetime-local o string vacío.
 */
export const convertUTCToMadridDateTimeLocalString = (dateInput) => {
  if (!dateInput) return '';
  try {
    console.log('[dateUtils] Input para toZonedTime:', dateInput);
    const zonedDate = toZonedTime(dateInput, TIME_ZONE); 
    console.log('[dateUtils] zonedDate object:', zonedDate);

    if (!(zonedDate instanceof Date) || isNaN(zonedDate.getTime())) {
      console.error('[dateUtils] zonedDate no es un objeto Date válido:', zonedDate);
      return '';
    }

    const year = zonedDate.getFullYear();
    const month = String(zonedDate.getMonth() + 1).padStart(2, '0');
    const day = String(zonedDate.getDate()).padStart(2, '0');
    const hours = String(zonedDate.getHours()).padStart(2, '0');
    const minutes = String(zonedDate.getMinutes()).padStart(2, '0');

    const result = `${year}-${month}-${day}T${hours}:${minutes}`;
    console.log('[dateUtils] Resultado manual para datetime-local:', result);
    return result;

  } catch (error) {
    console.error('[dateUtils] Error en convertUTCToMadridDateTimeLocalString con input:', dateInput, error);
    return '';
  }
};

/**
 * Convierte un string YYYY-MM-DDTHH:MM (de un input datetime-local, interpretado como hora de Madrid)
 * a un string ISO UTC.
 * @param {string} localDateTimeString - El string en formato YYYY-MM-DDTHH:MM.
 * @returns {string | null} - La fecha en formato ISO UTC o null si la entrada es inválida.
 */
export const convertMadridDateTimeLocalToUTCString = (localDateTimeString) => {
  if (!localDateTimeString) return null;
  try {
    // Aquí parseISO es importante porque localDateTimeString NO tiene info de zona horaria.
    // parseISO lo tratará como "local" según el sistema, pero fromZonedTime luego
    // lo interpretará correctamente como si esos componentes fueran de TIME_ZONE.
    const date = parseISO(localDateTimeString);
    const utcDate = fromZonedTime(date, TIME_ZONE);
    return utcDate.toISOString();
  } catch (error) {
    console.error('[dateUtils] Error en convertMadridDateTimeLocalToUTCString con input:', localDateTimeString, error);
    return null;
  }
};

/**
 * Convierte un string de fecha YYYY-MM-DD (de un input date, interpretado como fecha de Madrid)
 * al inicio de ese día en Madrid, y luego a un string ISO UTC.
 * @param {string} localDateString - El string en formato YYYY-MM-DD.
 * @returns {string | null} - La fecha en formato ISO UTC (inicio del día en Madrid) o null.
 */
export const convertMadridDateToUTCString = (localDateString) => {
  if (!localDateString) return null;
  try {
    const localDateTimeString = `${localDateString}T00:00:00`;
    // Similar al anterior, parseISO para obtener un objeto Date, luego fromZonedTime para la conversión correcta.
    const date = parseISO(localDateTimeString);
    const utcDate = fromZonedTime(date, TIME_ZONE);
    return utcDate.toISOString();
  } catch (error) {
    console.error('[dateUtils] Error en convertMadridDateToUTCString con input:', localDateString, error);
    return null;
  }
}; 